import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";

import { BlueprintCard } from "./BlueprintCard";

import { useBlueprints } from "@/api/creativesphere";
import { CloseIcon } from "@/icons";
import { type BlueprintMessage } from "@/types/creativesphere";

export interface BlueprintPickerModalProps {
  open: boolean;
  siteId: string;
  onClose: () => void;
  onSelect: (blueprint: BlueprintMessage) => void;
}

/**
 * Modal for picking the blueprint. Opens with an unfiltered blueprint
 * so the user sees blueprints immediately, and selects a blueprint closes the modal.
 */
export function BlueprintPickerModal({
  siteId,
  open,
  onClose,
  onSelect,
}: BlueprintPickerModalProps) {
  const { data, isLoading } = useBlueprints(siteId);
  const blueprints = data?.blueprints ?? [];
  const hasResults = blueprints?.length > 0;

  const handleClose = () => {
    onClose();
  };

  const handleSelect = (blueprint: BlueprintMessage) => {
    onSelect(blueprint);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: 1024 } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h6" component="span">
            Choose a blueprint
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={handleClose}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 10,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {hasResults ? (
              blueprints.map((blueprint) => (
                <BlueprintCard
                  key={blueprint.slug}
                  blueprint={blueprint}
                  onSelect={handleSelect}
                />
              ))
            ) : (
              <Typography variant="body1">No blueprints found</Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
